import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import Logo from "@/components/Logo";
import { useLang } from "@/lib/i18n";

const LINKS = [
  { id: "philosophy", key: "nav.philosophy", testId: "nav-link-philosophy" },
  { id: "services", key: "nav.services", testId: "nav-link-services" },
  { id: "portfolio", key: "nav.portfolio", testId: "nav-link-portfolio" },
  { id: "faq", key: "nav.faq", testId: "nav-link-faq" },
];

function LangToggle({ idSuffix = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid={`lang-toggle${idSuffix}`}
      role="group"
      aria-label="Language / Langue"
      className="flex items-center rounded-full border border-[#262930] bg-[#121418] p-0.5"
    >
      {["en", "fr"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}${idSuffix}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-2.5 py-1.5 font-tech text-[10px] uppercase tracking-[0.12em] transition-colors duration-200 ${
            lang === l ? "bg-[#8C9A86] text-[#0B0C0E] font-semibold" : "text-[#9E9E9E] hover:text-[#EAE6E1]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-[#262930] bg-[#0B0C0E]/80 backdrop-blur-xl pt-[env(safe-area-inset-top)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("hero")}
          className="flex items-center gap-3 group py-2"
        >
          <Logo />
          <span className="hidden md:flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76]">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4ADE80] opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#4ADE80]" />
            </span>
            {t("nav.status")}
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={link.testId}
              onClick={() => go(link.id)}
              className="text-sm text-[#9E9E9E] hover:text-[#EAE6E1] transition-colors duration-200"
            >
              {t(link.key)}
            </button>
          ))}
          <LangToggle />
          <button
            data-testid="nav-cta-start-project"
            onClick={() => go("contact")}
            className="group flex items-center gap-1.5 rounded-full bg-[#8C9A86] px-5 py-2 text-sm font-semibold text-[#0B0C0E] transition-colors duration-200 hover:bg-[#9FAF98]"
          >
            {t("nav.startProject")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#EAE6E1] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-t border-[#262930] bg-[#0B0C0E]/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`mobile-${link.testId}`}
                  onClick={() => go(link.id)}
                  className="text-left text-base text-[#9E9E9E] hover:text-[#EAE6E1] transition-colors duration-200"
                >
                  {t(link.key)}
                </button>
              ))}
              <LangToggle idSuffix="-mobile" />
              <button
                data-testid="mobile-nav-cta-start-project"
                onClick={() => go("contact")}
                className="mt-2 flex w-fit items-center gap-1.5 rounded-full bg-[#8C9A86] px-5 py-2.5 text-sm font-semibold text-[#0B0C0E]"
              >
                {t("nav.startProject")}
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
